/**
 * EP (Cero Fuga) leads dashboard contract.
 * Source: GET /api/ep/dashboard?tab=with_bot|to_contact|incomplete|to_send_to_analysis|approved|archived (DEV-8166 / DEV-8167)
 */

export type EpLeadTab = "with_bot" | "to_contact" | "incomplete" | "to_send_to_analysis" | "approved" | "archived";

export type EpLeadSeverity = "high" | "medium" | "info" | "success";

export type EpLeadActionStyle = "primary" | "secondary";

/** CTAs rendered by the EP cards, matching the Figma designs and the backend action catalog. */
export type EpLeadActionCode =
  | "register_attempt"
  | "contact_client"
  | "contact_salesman"
  | "quote"
  | "view_details"
  | "view_chat"
  | "archive"
  | "unarchive";

export interface EpLeadAttentionReason {
  code: string;
  /** Optional human title; the UI derives the pill label from `code` when absent. */
  title?: string;
  description: string;
  severity: EpLeadSeverity;
  /** ISO8601 estimated return time; only present on the `agent_resuming` sub-status. */
  resume_at?: string;
}

export interface EpLeadAction {
  code: EpLeadActionCode | string;
  /** Optional; the UI derives a label from `code` when absent. */
  label?: string;
  /** Optional; the UI derives primary/secondary from the action set when absent. */
  style?: EpLeadActionStyle;
}

/** Inactivity badge computed by the backend serializer (DEV-8167). */
export interface EpLeadInactivityAlert {
  active: boolean;
  stale: boolean;
  /** Null unless `active` is true. */
  label: string | null;
  /** Null unless `active` is true. */
  description: string | null;
}

/** Status of a single onboarding step shown in the "Ver detalles" checklist modal. */
export type EpOnboardingStepStatus = "pending" | "in_progress" | "done" | "error";

/** One onboarding step of the "Ver detalles" checklist (Comprobante domicilio, Referencias, ...). */
export interface EpOnboardingStep {
  /** Stable step key, e.g. "address_proof" | "bank_statements" | "identity" | "incomes" | "references". */
  key: string;
  /** Optional human label; the UI derives one from `key` when absent. */
  label?: string;
  status: EpOnboardingStepStatus;
  /** Short blocker note under the label (e.g. "Faltan 2 referencias"); only on `error` steps. */
  note?: string | null;
}

/** A single approved condition; `original` is only present when the analyst changed it. */
export interface EpApprovalCondition {
  /** Stable key, e.g. "product" | "amount" | "down_payment" | "required_income". */
  key: string;
  /** Optional human label; the UI derives one from `key` when absent. */
  label?: string;
  /** Approved (current) value, preformatted by the backend (e.g. "$430,000.00"). */
  value: string;
  /** Original value before the analyst's change; only on "Aprobado con condiciones". */
  original?: string | null;
}

export interface EpLead {
  /** Card id (ExternalQuote.id); the path param for ALL per-lead action endpoints. Always present. */
  id: number;
  /** Person id; informative only (the applicant, when known). May be null; never used for addressing. */
  person_id?: number | null;
  /** Contact id (contact level, groups several persons/quotes); used only to navigate to /clients/:id. */
  contact_id: number;
  /** Application id, when available, to deep-link into the application sub-views (?id=). */
  application_id?: number | null;
  /** Approved quote id, to deep-link into the quoter (?nav=quotes|quotesV2&id=). */
  quote_id?: number | null;
  /** Quoting engine of the approved quote; selects quotes (legacy) vs quotesV2 nav. */
  quoting_engine?: "legacy" | "v2" | null;
  name: string;
  priority_label: string | null;
  dealership: string | null;
  /** EP dueño del lead — solo viene en la vista agregada de gerente (la card lo muestra junto a la agencia). */
  ep_name?: string | null;
  brand: string | null;
  last_activity_at: string | null;
  /**
   * Onboarding status reason powering the card's status pill + description.
   * Archived leads keep it too (the archived card shows the status it would have when active);
   * it can still be null for states without a reason.
   */
  attention_reason: EpLeadAttentionReason | null;
  available_actions: EpLeadAction[];
  /** Onboarding checklist for the "Ver detalles" modal; absent on backends that don't send it yet. */
  onboarding_steps?: EpOnboardingStep[];
  /** Approval conditions for the "Ver detalles de la aprobación" modal (approved tab only). */
  approval_conditions?: EpApprovalCondition[];
  inactivity_days?: number;
  inactivity_alert?: EpLeadInactivityAlert;
  /**
   * Eliza (el agente) condujo el onboarding de este lead. Sigue en `true` cuando el lead
   * ya salió de la pestaña "Con Eliza", así que la card marca su origen en cualquier tab.
   * Ausente en backends que no lo envían todavía (la card no muestra el logo).
   */
  agent_sourced?: boolean;
  /** Set only for archived leads (tab=archived); used to switch the card to its archived layout. */
  archived_at?: string | null;
}

export interface EpDashboardCounts {
  /** Leads still handled by the bot; hidden in the UI until the bot ships. */
  with_bot?: number;
  to_contact: number;
  /** Leads with an incomplete onboarding (formerly `to_attend`). */
  incomplete: number;
  /** Leads ready to be sent to analysis. */
  to_send_to_analysis?: number;
  approved: number;
  /** Present in every dashboard response; powers the "Ver clientes archivados (N)" link. */
  archived_count?: number;
}

/** Pagination metadata for the active tab (DEV-8170). */
export interface EpDashboardMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface EpDashboardResponse {
  counts: EpDashboardCounts;
  data: EpLead[];
  /** Pagination info for the requested tab; absent on backends that don't paginate yet. */
  meta?: EpDashboardMeta;
}

/** Response of POST /api/ep/leads/:cardId/unarchive; `tab` is where the lead landed. */
export interface EpUnarchiveResponse {
  tab?: EpLeadTab;
}

/** WhatsApp link recipient for GET /api/ep/leads/:cardId/whatsapp_link (defaults to `client`). */
export type EpWhatsappRecipient = "client" | "salesman";

export interface EpWhatsappLinkResponse {
  url: string;
}

/**
 * Bot conversation contract for the EP "observe" panel.
 * Source: GET /api/ep/leads/:cardId/conversation — Rails reads Chatwoot
 * server-side and returns the normalized messages (the front never holds the
 * Chatwoot token).
 */

/** Who sent a message: the AI bot, a human advisor, or the client. */
export type EpConversationSender = "bot" | "agent" | "client";

/** A file attachment within a message (e.g. an uploaded PDF or image). */
export interface EpConversationAttachment {
  file_name: string;
  /** Human-readable size, e.g. "245 KB". */
  file_size?: string | null;
  /** Chatwoot file type: "image" | "file" | "audio" | "video". */
  file_type?: string | null;
  /** Thumbnail URL for images (used for the inline preview). */
  thumb_url?: string | null;
  /** Optional direct URL to open/preview the file. */
  url?: string | null;
}

/** A single message in the Chatwoot conversation (normalized by the backend). */
export interface EpConversationMessage {
  id: number | string;
  sender: EpConversationSender;
  /** Display name (e.g. "Bot", "Mario Ramírez Mejía"); UI derives one when absent. */
  sender_name?: string | null;
  /** Message text; may be empty when the message is only an attachment. */
  content?: string | null;
  /** ISO timestamp; the UI shows its HH:MM:SS next to the bubble. */
  sent_at: string;
  attachments?: EpConversationAttachment[];
}

/** Conversation shown in the bot panel (GET /api/ep/leads/:cardId/conversation). */
export interface EpConversation {
  /** Channel label, e.g. "WhatsApp" — shown in the "Conversación iniciada" banner. */
  channel: string;
  /** Conversation status label, e.g. "Activo" — shown in the banner. */
  status: string;
  /** ISO timestamp the conversation started; the banner shows its time. */
  started_at: string | null;
  /** Whether the conversation is still active ("En vivo") vs closed. */
  live: boolean;
  /** Who is answering: the AI bot or a human advisor (drives "En vivo con X"). */
  handler?: "ai" | "human";
  /** Human advisor's name when handler is "human" (e.g. "Eliza"). */
  handler_name?: string | null;
  /** Client display name for the panel header. */
  client_name?: string | null;
  /** Whether the agent is currently typing (drives the typing indicator). */
  typing?: boolean;
  messages: EpConversationMessage[];
}
